export type StreamStatusV2 = 'online' | 'inestable' | 'offline' | 'error' | 'sin_respuesta';

export type HealthStatus = 'excelente' | 'buena' | 'regular' | 'inactiva' | 'suspendida';

export type RadioPlan = 'free' | 'pro' | 'premium';

export type RecommendedAction =
  | 'notificar'
  | 'advertencia'
  | 'ocultar'
  | 'en_riesgo'
  | 'recomendar_suspension'
  | 'suspender'
  | 'liberar_frecuencia'
  | 'ignorar';

export interface RadioHealthData {
  radioId: number;
  radioName: string;
  radioSlug: string;
  frequency: string;
  city: string;
  state: string;
  category: string;
  logoUrl: string;
  plan: RadioPlan;
  score: number;
  healthStatus: HealthStatus;
  streamStatus: StreamStatusV2;
  lastStream: string | null;
  lastSuccessfulStream: string | null;
  lastPublication: string | null;
  lastEvent: string | null;
  lastLive: string | null;
  lastCabinaActivity: string | null;
  lastAdminAccess: string | null;
  listenerResponseRate: number;
  daysSinceLastActivity: number;
  daysOffline: number;
  warningLevel: 'ninguna' | 'advertencia' | 'oculta' | 'suspendida' | 'en_riesgo' | 'critica';
  warningMessage: string | null;
  recommendedAction: RecommendedAction;
  isHiddenFromDirectory: boolean;
  isAtRisk: boolean;
  notified24h: boolean;
  notified3d: boolean;
  notified7d: boolean;
  notified15d: boolean;
  totalPublications: number;
  totalEvents: number;
  totalLives: number;
  totalCabinaMessages: number;
}

/* ─── Labels & Colors ─── */
export const healthStatusLabels: Record<HealthStatus, string> = {
  excelente: 'Excelente',
  buena: 'Buena',
  regular: 'Regular',
  inactiva: 'Inactiva',
  suspendida: 'Suspendida',
};

export const healthStatusColors: Record<HealthStatus, { bg: string; text: string; dot: string }> = {
  excelente: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  buena: { bg: 'bg-sky-100', text: 'text-sky-700', dot: 'bg-sky-500' },
  regular: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  inactiva: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  suspendida: { bg: 'bg-foreground-200', text: 'text-foreground-600', dot: 'bg-foreground-500' },
};

export const streamStatusLabelsV2: Record<StreamStatusV2, string> = {
  online: 'Online',
  inestable: 'Inestable',
  offline: 'Offline',
  error: 'Error',
  sin_respuesta: 'Sin respuesta',
};

export const streamStatusColorsV2: Record<StreamStatusV2, { bg: string; text: string; dot: string; icon: string }> = {
  online: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: 'ri-checkbox-circle-fill' },
  inestable: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', icon: 'ri-error-warning-fill' },
  offline: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', icon: 'ri-close-circle-fill' },
  error: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', icon: 'ri-bug-fill' },
  sin_respuesta: { bg: 'bg-foreground-100', text: 'text-foreground-600', dot: 'bg-foreground-400', icon: 'ri-question-fill' },
};

export const recommendedActionLabels: Record<RecommendedAction, string> = {
  notificar: 'Notificar radio',
  advertencia: 'Advertencia fuerte',
  ocultar: 'Ocultar del directorio',
  en_riesgo: 'Marcar En Riesgo',
  recomendar_suspension: 'Recomendar suspensión',
  suspender: 'Suspender',
  liberar_frecuencia: 'Liberar frecuencia',
  ignorar: 'Ignorar por ahora',
};

export const recommendedActionColors: Record<RecommendedAction, { bg: string; text: string; icon: string }> = {
  notificar: { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-notification-3-line' },
  advertencia: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'ri-error-warning-line' },
  ocultar: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'ri-eye-off-line' },
  en_riesgo: { bg: 'bg-red-100', text: 'text-red-700', icon: 'ri-alert-line' },
  recomendar_suspension: { bg: 'bg-red-100', text: 'text-red-700', icon: 'ri-forbid-line' },
  suspender: { bg: 'bg-red-200', text: 'text-red-800', icon: 'ri-stop-circle-line' },
  liberar_frecuencia: { bg: 'bg-foreground-200', text: 'text-foreground-700', icon: 'ri-delete-back-2-line' },
  ignorar: { bg: 'bg-foreground-100', text: 'text-foreground-500', icon: 'ri-time-line' },
};

export const warningLabels: Record<string, string> = {
  ninguna: 'Sin alertas',
  advertencia: 'Advertencia',
  oculta: 'Oculta',
  en_riesgo: 'En Riesgo',
  critica: 'Crítica',
  suspendida: 'Suspendida',
};

export const warningColors: Record<string, { bg: string; text: string }> = {
  ninguna: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  advertencia: { bg: 'bg-amber-50', text: 'text-amber-600' },
  oculta: { bg: 'bg-orange-50', text: 'text-orange-600' },
  en_riesgo: { bg: 'bg-red-50', text: 'text-red-600' },
  critica: { bg: 'bg-red-100', text: 'text-red-700' },
  suspendida: { bg: 'bg-foreground-100', text: 'text-foreground-600' },
};

/* ─── Helpers ─── */
function daysSince(dateStr: string | null): number {
  if (!dateStr) return 999;
  const d = new Date(dateStr);
  const now = new Date('2026-06-08T22:00:00');
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function calculateScore(data: Partial<RadioHealthData>): number {
  let s = 50;
  if (data.streamStatus === 'online') s += 25;
  else if (data.streamStatus === 'inestable') s += 10;
  if (data.lastPublication && daysSince(data.lastPublication) < 7) s += 10;
  if (data.lastLive && daysSince(data.lastLive) < 14) s += 5;
  if (data.lastEvent && daysSince(data.lastEvent) < 30) s += 3;
  if (data.lastCabinaActivity && daysSince(data.lastCabinaActivity) < 3) s += 5;
  if (data.listenerResponseRate && data.listenerResponseRate > 0.7) s += 2;
  return Math.min(100, Math.max(0, s));
}

/* ─── Progressive rules engine ─── */
export function computeStreamDownState(
  plan: RadioPlan,
  daysOffline: number,
): {
  warningLevel: string;
  warningMessage: string | null;
  recommendedAction: RecommendedAction;
  isHidden: boolean;
  isAtRisk: boolean;
  notified24h: boolean;
  notified3d: boolean;
  notified7d: boolean;
  notified15d: boolean;
} {
  const base = {
    isHidden: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
  };

  // General rules (apply to all plans)
  if (daysOffline >= 1) {
    base.notified24h = true;
  }

  if (plan === 'free') {
    // FREE — stricter
    if (daysOffline >= 30) {
      return {
        ...base,
        warningLevel: 'suspendida',
        warningMessage: '30 días sin stream — Liberar frecuencia RFW',
        recommendedAction: 'liberar_frecuencia',
        isHidden: true,
        isAtRisk: true,
        notified3d: true,
        notified7d: true,
        notified15d: true,
      };
    }
    if (daysOffline >= 15) {
      return {
        ...base,
        warningLevel: 'suspendida',
        warningMessage: '15 días sin stream — Suspensión automática aplicada',
        recommendedAction: 'suspender',
        isHidden: true,
        isAtRisk: true,
        notified3d: true,
        notified7d: true,
      };
    }
    if (daysOffline >= 7) {
      return {
        ...base,
        warningLevel: 'oculta',
        warningMessage: '7 días sin stream — Ocultada del directorio automáticamente',
        recommendedAction: 'ocultar',
        isHidden: true,
        isAtRisk: false,
        notified3d: true,
      };
    }
    if (daysOffline >= 3) {
      return {
        ...base,
        warningLevel: 'advertencia',
        warningMessage: '3 días sin stream — Advertencia fuerte',
        recommendedAction: 'advertencia',
        isHidden: false,
        isAtRisk: false,
        notified3d: true,
      };
    }
    if (daysOffline >= 1) {
      return {
        ...base,
        warningLevel: 'advertencia',
        warningMessage: '24 horas sin stream — Aviso enviado a Admin Radio',
        recommendedAction: 'notificar',
        isHidden: false,
        isAtRisk: false,
      };
    }
    return {
      ...base,
      warningLevel: 'ninguna',
      warningMessage: null,
      recommendedAction: 'ignorar',
    };
  }

  // PRO / PREMIUM — more careful
  if (daysOffline >= 30) {
    return {
      ...base,
      warningLevel: 'critica',
      warningMessage: '30 días sin stream — Super Admin debe decidir suspensión',
      recommendedAction: 'recomendar_suspension',
      isHidden: true,
      isAtRisk: true,
      notified3d: true,
      notified7d: true,
      notified15d: true,
    };
  }
  if (daysOffline >= 15) {
    return {
      ...base,
      warningLevel: 'en_riesgo',
      warningMessage: '15 días sin stream — Aviso crítico enviado',
      recommendedAction: 'en_riesgo',
      isHidden: true,
      isAtRisk: true,
      notified3d: true,
      notified7d: true,
    };
  }
  if (daysOffline >= 7) {
    return {
      ...base,
      warningLevel: 'oculta',
      warningMessage: '7 días sin stream — Oculta temporalmente del directorio',
      recommendedAction: 'ocultar',
      isHidden: true,
      isAtRisk: false,
      notified3d: true,
    };
  }
  if (daysOffline >= 3) {
    return {
      ...base,
      warningLevel: 'advertencia',
      warningMessage: '3 días sin stream — Advertencia fuerte',
      recommendedAction: 'advertencia',
      isHidden: false,
      isAtRisk: false,
      notified3d: true,
    };
  }
  if (daysOffline >= 1) {
    return {
      ...base,
      warningLevel: 'advertencia',
      warningMessage: '24 horas sin stream — Aviso enviado a Admin Radio y Super Admin',
      recommendedAction: 'notificar',
      isHidden: false,
      isAtRisk: false,
    };
  }
  return {
    ...base,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
  };
}

/* ─── Mock radio health data ─── */
export const radioHealthData: RadioHealthData[] = [
  {
    radioId: 3,
    radioName: 'La Mexicana FM',
    radioSlug: 'la-mexicana-fm',
    frequency: '103.50 RFW',
    city: 'Houston',
    state: 'Texas',
    category: 'Regional Mexicana',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style%2C%20festive%20vibrant%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-3&orientation=squarish',
    plan: 'premium',
    score: 96,
    healthStatus: 'excelente',
    streamStatus: 'online',
    lastStream: '2026-06-08T20:30:00',
    lastSuccessfulStream: '2026-06-08T20:30:00',
    lastPublication: '2026-06-08T19:15:00',
    lastEvent: '2026-06-06T18:00:00',
    lastLive: '2026-06-07T21:00:00',
    lastCabinaActivity: '2026-06-08T20:45:00',
    lastAdminAccess: '2026-06-08T18:00:00',
    listenerResponseRate: 0.92,
    daysSinceLastActivity: 0,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 145,
    totalEvents: 12,
    totalLives: 8,
    totalCabinaMessages: 2340,
  },
  {
    radioId: 7,
    radioName: 'Deportes Total Radio',
    radioSlug: 'deportes-total-radio',
    frequency: '120.00 RFW',
    city: 'Dallas',
    state: 'Texas',
    category: 'Deportes',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%2C%20dynamic%20broadcast%20emblem%2C%20energetic%20sports%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-7&orientation=squarish',
    plan: 'pro',
    score: 91,
    healthStatus: 'excelente',
    streamStatus: 'online',
    lastStream: '2026-06-08T20:00:00',
    lastSuccessfulStream: '2026-06-08T20:00:00',
    lastPublication: '2026-06-08T18:30:00',
    lastEvent: '2026-06-05T16:00:00',
    lastLive: '2026-06-08T19:00:00',
    lastCabinaActivity: '2026-06-08T20:15:00',
    lastAdminAccess: '2026-06-08T17:30:00',
    listenerResponseRate: 0.88,
    daysSinceLastActivity: 0,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 210,
    totalEvents: 18,
    totalLives: 15,
    totalCabinaMessages: 3450,
  },
  {
    radioId: 11,
    radioName: 'Radio Vida Nueva',
    radioSlug: 'radio-vida-nueva',
    frequency: '180.00 RFW',
    city: 'Orlando',
    state: 'Florida',
    category: 'Cristiana',
    logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20worship%20radio%20station%20logo%20with%20cross%20and%20sunrise%20symbols%2C%20circular%20design%20soft%20blue%20and%20white%20colors%2C%20peaceful%20spiritual%20broadcast%20emblem%2C%20minimalist%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-11&orientation=squarish',
    plan: 'premium',
    score: 88,
    healthStatus: 'excelente',
    streamStatus: 'online',
    lastStream: '2026-06-08T19:45:00',
    lastSuccessfulStream: '2026-06-08T19:45:00',
    lastPublication: '2026-06-08T16:20:00',
    lastEvent: '2026-06-04T14:00:00',
    lastLive: '2026-06-07T20:00:00',
    lastCabinaActivity: '2026-06-08T19:30:00',
    lastAdminAccess: '2026-06-08T15:00:00',
    listenerResponseRate: 0.85,
    daysSinceLastActivity: 0,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 98,
    totalEvents: 7,
    totalLives: 5,
    totalCabinaMessages: 1890,
  },
  {
    radioId: 5,
    radioName: 'Rock Total Radio',
    radioSlug: 'rock-total-radio',
    frequency: '87.01 RFW',
    city: 'Los Ángeles',
    state: 'California',
    category: 'Rock',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-5&orientation=squarish',
    plan: 'pro',
    score: 85,
    healthStatus: 'excelente',
    streamStatus: 'online',
    lastStream: '2026-06-08T18:00:00',
    lastSuccessfulStream: '2026-06-08T18:00:00',
    lastPublication: '2026-06-07T22:00:00',
    lastEvent: '2026-06-01T20:00:00',
    lastLive: '2026-06-06T21:00:00',
    lastCabinaActivity: '2026-06-08T17:30:00',
    lastAdminAccess: '2026-06-07T14:00:00',
    listenerResponseRate: 0.79,
    daysSinceLastActivity: 0,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 167,
    totalEvents: 9,
    totalLives: 11,
    totalCabinaMessages: 2760,
  },
  {
    radioId: 1,
    radioName: 'Radio Nueva Actitud',
    radioSlug: 'radio-nueva-actitud',
    frequency: '94.70 RFW',
    city: 'Santa Rosa',
    state: 'California',
    category: 'Pop',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20high%20contrast%20vector%20style&width=200&height=200&seq=logo-1&orientation=squarish',
    plan: 'free',
    score: 78,
    healthStatus: 'buena',
    streamStatus: 'online',
    lastStream: '2026-06-08T15:00:00',
    lastSuccessfulStream: '2026-06-08T15:00:00',
    lastPublication: '2026-06-06T14:00:00',
    lastEvent: null,
    lastLive: '2026-06-04T19:00:00',
    lastCabinaActivity: '2026-06-08T14:30:00',
    lastAdminAccess: '2026-06-08T12:00:00',
    listenerResponseRate: 0.65,
    daysSinceLastActivity: 0,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 45,
    totalEvents: 0,
    totalLives: 3,
    totalCabinaMessages: 890,
  },
  {
    radioId: 2,
    radioName: 'Radio Fe y Esperanza',
    radioSlug: 'radio-fe-y-esperanza',
    frequency: '87.00 RFW',
    city: 'Miami',
    state: 'Florida',
    category: 'Cristiana',
    logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%2C%20circular%20minimalist%20design%2C%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%2C%20clean%20modern%20broadcast%20emblem%2C%20spiritual%20community%20radio%2C%20isolated%20on%20transparent%20background%2C%20vector%20style%20flat%20design&width=200&height=200&seq=logo-2&orientation=squarish',
    plan: 'free',
    score: 72,
    healthStatus: 'buena',
    streamStatus: 'inestable',
    lastStream: '2026-06-08T10:00:00',
    lastSuccessfulStream: '2026-06-08T10:00:00',
    lastPublication: '2026-06-05T09:00:00',
    lastEvent: '2026-05-28T16:00:00',
    lastLive: null,
    lastCabinaActivity: '2026-06-07T20:00:00',
    lastAdminAccess: '2026-06-08T08:00:00',
    listenerResponseRate: 0.71,
    daysSinceLastActivity: 0,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 34,
    totalEvents: 2,
    totalLives: 0,
    totalCabinaMessages: 560,
  },
  {
    radioId: 12,
    radioName: 'Banda Radio MX',
    radioSlug: 'banda-radio-mx',
    frequency: '200.00 RFW',
    city: 'San Antonio',
    state: 'Texas',
    category: 'Regional Mexicana',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%2C%20circular%20design%20red%20white%20and%20green%20colors%2C%20festive%20broadcast%20emblem%2C%20traditional%20Mexican%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-12&orientation=squarish',
    plan: 'pro',
    score: 68,
    healthStatus: 'buena',
    streamStatus: 'online',
    lastStream: '2026-06-08T14:00:00',
    lastSuccessfulStream: '2026-06-08T14:00:00',
    lastPublication: '2026-06-01T11:00:00',
    lastEvent: null,
    lastLive: '2026-05-30T18:00:00',
    lastCabinaActivity: '2026-06-07T16:00:00',
    lastAdminAccess: '2026-06-06T10:00:00',
    listenerResponseRate: 0.62,
    daysSinceLastActivity: 1,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 56,
    totalEvents: 0,
    totalLives: 4,
    totalCabinaMessages: 1230,
  },
  {
    radioId: 9,
    radioName: 'La Kalle 99.9',
    radioSlug: 'la-kalle-999',
    frequency: '99.90 RFW',
    city: 'Phoenix',
    state: 'Arizona',
    category: 'Pop',
    logoUrl: 'https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%2C%20circular%20design%20vibrant%20pink%20and%20purple%20colors%2C%20modern%20youthful%20broadcast%20emblem%2C%20energetic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-9&orientation=squarish',
    plan: 'free',
    score: 55,
    healthStatus: 'regular',
    streamStatus: 'inestable',
    lastStream: '2026-06-08T08:00:00',
    lastSuccessfulStream: '2026-06-08T08:00:00',
    lastPublication: '2026-05-30T15:00:00',
    lastEvent: null,
    lastLive: null,
    lastCabinaActivity: '2026-06-05T12:00:00',
    lastAdminAccess: '2026-06-07T09:00:00',
    listenerResponseRate: 0.45,
    daysSinceLastActivity: 3,
    daysOffline: 0,
    warningLevel: 'ninguna',
    warningMessage: null,
    recommendedAction: 'ignorar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: false,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 22,
    totalEvents: 0,
    totalLives: 0,
    totalCabinaMessages: 340,
  },

  /* ═══════ STREAM DOWN RADIOS — progressive stages ═══════ */

  // Stage 1: 1 day offline — notification sent
  {
    radioId: 21,
    radioName: 'Radio Onda Tropical',
    radioSlug: 'radio-onda-tropical',
    frequency: '92.50 RFW',
    city: 'San Juan',
    state: 'Puerto Rico',
    category: 'Pop',
    logoUrl: 'https://readdy.ai/api/search-image?query=Tropical%20radio%20station%20logo%20with%20palm%20tree%20and%20sunset%20sound%20waves%2C%20circular%20design%20warm%20orange%20and%20teal%20colors%2C%20Caribbean%20vibe%2C%20modern%20broadcast%20emblem%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-21&orientation=squarish',
    plan: 'pro',
    score: 62,
    healthStatus: 'buena',
    streamStatus: 'offline',
    lastStream: '2026-06-07T23:00:00',
    lastSuccessfulStream: '2026-06-07T22:45:00',
    lastPublication: '2026-06-07T18:00:00',
    lastEvent: null,
    lastLive: '2026-06-05T20:00:00',
    lastCabinaActivity: '2026-06-07T22:00:00',
    lastAdminAccess: '2026-06-08T10:00:00',
    listenerResponseRate: 0.58,
    daysSinceLastActivity: 1,
    daysOffline: 1,
    warningLevel: 'advertencia',
    warningMessage: '24 horas sin stream — Aviso enviado a Admin Radio y Super Admin',
    recommendedAction: 'notificar',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: true,
    notified3d: false,
    notified7d: false,
    notified15d: false,
    totalPublications: 67,
    totalEvents: 0,
    totalLives: 5,
    totalCabinaMessages: 1120,
  },

  // Stage 2: 3 days offline — strong warning (Free)
  {
    radioId: 22,
    radioName: 'FM Urbana Latina',
    radioSlug: 'fm-urbana-latina',
    frequency: '97.30 RFW',
    city: 'El Paso',
    state: 'Texas',
    category: 'Regional Mexicana',
    logoUrl: 'https://readdy.ai/api/search-image?query=Urban%20Latino%20radio%20station%20logo%20with%20city%20skyline%20and%20music%20notes%2C%20circular%20design%20purple%20and%20gold%20colors%2C%20modern%20urban%20broadcast%20emblem%2C%20street%20culture%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-22&orientation=squarish',
    plan: 'free',
    score: 45,
    healthStatus: 'regular',
    streamStatus: 'offline',
    lastStream: '2026-06-05T14:00:00',
    lastSuccessfulStream: '2026-06-05T13:50:00',
    lastPublication: '2026-06-04T10:00:00',
    lastEvent: null,
    lastLive: null,
    lastCabinaActivity: '2026-06-05T12:00:00',
    lastAdminAccess: '2026-06-06T09:00:00',
    listenerResponseRate: 0.32,
    daysSinceLastActivity: 3,
    daysOffline: 3,
    warningLevel: 'advertencia',
    warningMessage: '3 días sin stream — Advertencia fuerte',
    recommendedAction: 'advertencia',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: true,
    notified3d: true,
    notified7d: false,
    notified15d: false,
    totalPublications: 31,
    totalEvents: 0,
    totalLives: 0,
    totalCabinaMessages: 450,
  },

  // Stage 2B: 3 days offline (Pro)
  {
    radioId: 23,
    radioName: 'Estación Digital 101',
    radioSlug: 'estacion-digital-101',
    frequency: '101.10 RFW',
    city: 'Austin',
    state: 'Texas',
    category: 'Internacional',
    logoUrl: 'https://readdy.ai/api/search-image?query=Digital%20radio%20station%20logo%20with%20globe%20and%20binary%20waves%2C%20circular%20minimalist%20design%20teal%20and%20white%20colors%2C%20tech%20modern%20broadcast%20emblem%2C%20digital%20native%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-23&orientation=squarish',
    plan: 'pro',
    score: 58,
    healthStatus: 'regular',
    streamStatus: 'offline',
    lastStream: '2026-06-05T16:00:00',
    lastSuccessfulStream: '2026-06-05T15:55:00',
    lastPublication: '2026-06-05T12:00:00',
    lastEvent: '2026-05-20T18:00:00',
    lastLive: '2026-06-03T20:00:00',
    lastCabinaActivity: '2026-06-05T15:00:00',
    lastAdminAccess: '2026-06-06T14:00:00',
    listenerResponseRate: 0.55,
    daysSinceLastActivity: 3,
    daysOffline: 3,
    warningLevel: 'advertencia',
    warningMessage: '3 días sin stream — Advertencia fuerte',
    recommendedAction: 'advertencia',
    isHiddenFromDirectory: false,
    isAtRisk: false,
    notified24h: true,
    notified3d: true,
    notified7d: false,
    notified15d: false,
    totalPublications: 89,
    totalEvents: 3,
    totalLives: 7,
    totalCabinaMessages: 1560,
  },

  // Stage 3: 7 days offline — hidden (Free, auto-hidden)
  {
    radioId: 24,
    radioName: 'Radio Impacto Joven',
    radioSlug: 'radio-impacto-joven',
    frequency: '104.20 RFW',
    city: 'Albuquerque',
    state: 'Nuevo México',
    category: 'Pop',
    logoUrl: 'https://readdy.ai/api/search-image?query=Youth%20radio%20station%20logo%20with%20lightning%20and%20star%20symbols%2C%20circular%20design%20electric%20green%20and%20black%20colors%2C%20energetic%20youth%20broadcast%20emblem%2C%20dynamic%20and%20modern%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-24&orientation=squarish',
    plan: 'free',
    score: 32,
    healthStatus: 'inactiva',
    streamStatus: 'offline',
    lastStream: '2026-06-01T08:00:00',
    lastSuccessfulStream: '2026-06-01T07:50:00',
    lastPublication: '2026-05-30T14:00:00',
    lastEvent: null,
    lastLive: null,
    lastCabinaActivity: '2026-05-31T18:00:00',
    lastAdminAccess: '2026-06-02T10:00:00',
    listenerResponseRate: 0.18,
    daysSinceLastActivity: 7,
    daysOffline: 7,
    warningLevel: 'oculta',
    warningMessage: '7 días sin stream — Ocultada del directorio automáticamente',
    recommendedAction: 'ocultar',
    isHiddenFromDirectory: true,
    isAtRisk: false,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: false,
    totalPublications: 14,
    totalEvents: 0,
    totalLives: 0,
    totalCabinaMessages: 210,
  },

  // Stage 3B: 7 days offline (Premium, temporary hide)
  {
    radioId: 25,
    radioName: 'Sinfonía Clásica Digital',
    radioSlug: 'sinfonia-clasica-digital',
    frequency: '96.80 RFW',
    city: 'Boston',
    state: 'Massachusetts',
    category: 'Internacional',
    logoUrl: 'https://readdy.ai/api/search-image?query=Classical%20music%20radio%20station%20logo%20with%20violin%20and%20music%20sheet%20scroll%2C%20circular%20elegant%20design%20burgundy%20and%20cream%20colors%2C%20refined%20sophisticated%20broadcast%20emblem%2C%20classical%20concert%20hall%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-25&orientation=squarish',
    plan: 'premium',
    score: 48,
    healthStatus: 'regular',
    streamStatus: 'error',
    lastStream: '2026-06-01T12:00:00',
    lastSuccessfulStream: '2026-06-01T09:30:00',
    lastPublication: '2026-06-01T10:00:00',
    lastEvent: '2026-05-15T19:00:00',
    lastLive: null,
    lastCabinaActivity: '2026-06-01T11:00:00',
    lastAdminAccess: '2026-06-03T15:00:00',
    listenerResponseRate: 0.60,
    daysSinceLastActivity: 7,
    daysOffline: 7,
    warningLevel: 'oculta',
    warningMessage: '7 días sin stream — Oculta temporalmente del directorio',
    recommendedAction: 'ocultar',
    isHiddenFromDirectory: true,
    isAtRisk: false,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: false,
    totalPublications: 134,
    totalEvents: 5,
    totalLives: 0,
    totalCabinaMessages: 890,
  },

  // Stage 4: 15 days offline — En Riesgo (Free, suspended)
  {
    radioId: 26,
    radioName: 'Noticias Hoy Digital',
    radioSlug: 'noticias-hoy-digital',
    frequency: '109.40 RFW',
    city: 'Tucson',
    state: 'Arizona',
    category: 'Noticias',
    logoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20with%20newspaper%20and%20antenna%20icon%2C%20circular%20design%20navy%20blue%20and%20red%20colors%2C%20journalism%20broadcast%20emblem%2C%20breaking%20news%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-26&orientation=squarish',
    plan: 'free',
    score: 18,
    healthStatus: 'suspendida',
    streamStatus: 'offline',
    lastStream: '2026-05-24T06:00:00',
    lastSuccessfulStream: '2026-05-24T05:55:00',
    lastPublication: '2026-05-20T08:00:00',
    lastEvent: null,
    lastLive: null,
    lastCabinaActivity: '2026-05-23T16:00:00',
    lastAdminAccess: '2026-05-25T09:00:00',
    listenerResponseRate: 0.08,
    daysSinceLastActivity: 15,
    daysOffline: 15,
    warningLevel: 'suspendida',
    warningMessage: '15 días sin stream — Suspensión automática aplicada',
    recommendedAction: 'suspender',
    isHiddenFromDirectory: true,
    isAtRisk: true,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: true,
    totalPublications: 9,
    totalEvents: 0,
    totalLives: 0,
    totalCabinaMessages: 85,
  },

  // Stage 4B: 15 days offline (Premium, critical notice)
  {
    radioId: 27,
    radioName: 'Radio Jazz & Blues',
    radioSlug: 'radio-jazz-blues',
    frequency: '112.60 RFW',
    city: 'New Orleans',
    state: 'Louisiana',
    category: 'Rock',
    logoUrl: 'https://readdy.ai/api/search-image?query=Jazz%20radio%20station%20logo%20with%20saxophone%20and%20smoky%20notes%2C%20circular%20design%20deep%20purple%20and%20gold%20colors%2C%20sophisticated%20blues%20broadcast%20emblem%2C%20New%20Orleans%20nightclub%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-27&orientation=squarish',
    plan: 'premium',
    score: 35,
    healthStatus: 'inactiva',
    streamStatus: 'error',
    lastStream: '2026-05-24T20:00:00',
    lastSuccessfulStream: '2026-05-23T18:00:00',
    lastPublication: '2026-05-22T15:00:00',
    lastEvent: '2026-05-10T20:00:00',
    lastLive: null,
    lastCabinaActivity: '2026-05-24T19:00:00',
    lastAdminAccess: '2026-05-26T12:00:00',
    listenerResponseRate: 0.42,
    daysSinceLastActivity: 15,
    daysOffline: 15,
    warningLevel: 'en_riesgo',
    warningMessage: '15 días sin stream — Aviso crítico enviado',
    recommendedAction: 'en_riesgo',
    isHiddenFromDirectory: true,
    isAtRisk: true,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: true,
    totalPublications: 72,
    totalEvents: 6,
    totalLives: 0,
    totalCabinaMessages: 1340,
  },

  // Stage 5: 30 days offline (Free, release frequency)
  {
    radioId: 28,
    radioName: 'Radio Comunidad del Valle',
    radioSlug: 'radio-comunidad-del-valle',
    frequency: '88.30 RFW',
    city: 'Fresno',
    state: 'California',
    category: 'Comunitaria',
    logoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20farm%20fields%20and%20sun%20rays%2C%20circular%20design%20earthy%20green%20and%20brown%20colors%2C%20rural%20community%20broadcast%20emblem%2C%20agricultural%20valley%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-28&orientation=squarish',
    plan: 'free',
    score: 8,
    healthStatus: 'suspendida',
    streamStatus: 'sin_respuesta',
    lastStream: '2026-05-09T08:00:00',
    lastSuccessfulStream: '2026-05-07T12:00:00',
    lastPublication: '2026-05-01T10:00:00',
    lastEvent: null,
    lastLive: null,
    lastCabinaActivity: '2026-05-05T14:00:00',
    lastAdminAccess: '2026-05-10T08:00:00',
    listenerResponseRate: 0.03,
    daysSinceLastActivity: 30,
    daysOffline: 30,
    warningLevel: 'suspendida',
    warningMessage: '30 días sin stream — Liberar frecuencia RFW',
    recommendedAction: 'liberar_frecuencia',
    isHiddenFromDirectory: true,
    isAtRisk: true,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: true,
    totalPublications: 4,
    totalEvents: 0,
    totalLives: 0,
    totalCabinaMessages: 30,
  },

  // Stage 5B: 30 days offline (Pro, suspension recommendation)
  {
    radioId: 29,
    radioName: 'Radio Deportiva Nacional',
    radioSlug: 'radio-deportiva-nacional',
    frequency: '131.50 RFW',
    city: 'Denver',
    state: 'Colorado',
    category: 'Deportes',
    logoUrl: 'https://readdy.ai/api/search-image?query=National%20sports%20radio%20station%20logo%20with%20stadium%20and%20microphone%2C%20circular%20design%20bold%20red%20and%20white%20colors%2C%20sports%20commentary%20broadcast%20emblem%2C%20major%20league%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-29&orientation=squarish',
    plan: 'pro',
    score: 25,
    healthStatus: 'suspendida',
    streamStatus: 'offline',
    lastStream: '2026-05-08T18:00:00',
    lastSuccessfulStream: '2026-05-06T20:00:00',
    lastPublication: '2026-05-05T16:00:00',
    lastEvent: null,
    lastLive: '2026-05-01T19:00:00',
    lastCabinaActivity: '2026-05-07T15:00:00',
    lastAdminAccess: '2026-05-09T10:00:00',
    listenerResponseRate: 0.28,
    daysSinceLastActivity: 30,
    daysOffline: 30,
    warningLevel: 'critica',
    warningMessage: '30 días sin stream — Super Admin debe decidir suspensión',
    recommendedAction: 'recomendar_suspension',
    isHiddenFromDirectory: true,
    isAtRisk: true,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: true,
    totalPublications: 55,
    totalEvents: 0,
    totalLives: 10,
    totalCabinaMessages: 780,
  },

  // Stage 5C: 30 days offline (Premium, Super Admin decides)
  {
    radioId: 30,
    radioName: 'Premium Hits Radio',
    radioSlug: 'premium-hits-radio',
    frequency: '99.99 RFW',
    city: 'Las Vegas',
    state: 'Nevada',
    category: 'Pop',
    logoUrl: 'https://readdy.ai/api/search-image?query=Premium%20music%20radio%20station%20logo%20with%20crown%20and%20gold%20records%2C%20circular%20luxury%20design%20black%20and%20gold%20colors%2C%20exclusive%20broadcast%20emblem%2C%20VIP%20premium%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-30&orientation=squarish',
    plan: 'premium',
    score: 30,
    healthStatus: 'inactiva',
    streamStatus: 'offline',
    lastStream: '2026-05-08T22:00:00',
    lastSuccessfulStream: '2026-05-07T20:00:00',
    lastPublication: '2026-05-03T18:00:00',
    lastEvent: '2026-04-28T20:00:00',
    lastLive: '2026-05-02T21:00:00',
    lastCabinaActivity: '2026-05-06T16:00:00',
    lastAdminAccess: '2026-05-08T14:00:00',
    listenerResponseRate: 0.50,
    daysSinceLastActivity: 30,
    daysOffline: 30,
    warningLevel: 'critica',
    warningMessage: '30 días sin stream — Super Admin debe decidir suspensión o mantener activa',
    recommendedAction: 'recomendar_suspension',
    isHiddenFromDirectory: true,
    isAtRisk: true,
    notified24h: true,
    notified3d: true,
    notified7d: true,
    notified15d: true,
    totalPublications: 160,
    totalEvents: 14,
    totalLives: 22,
    totalCabinaMessages: 2890,
  },
];

/* ─── Maintain backward compat for old stream status ─── */
export type StreamStatus = 'activo' | 'inestable' | 'fuera_linea';

export const streamStatusLabels: Record<StreamStatus, string> = {
  activo: 'Activo',
  inestable: 'Inestable',
  fuera_linea: 'Fuera de línea',
};

export const streamStatusColors: Record<StreamStatus, { bg: string; text: string; dot: string; icon: string }> = {
  activo: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: 'ri-checkbox-circle-fill' },
  inestable: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', icon: 'ri-error-warning-fill' },
  fuera_linea: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', icon: 'ri-close-circle-fill' },
};

/* ─── Derived views ─── */
export const mostActiveRadios = [...radioHealthData]
  .filter((r) => r.streamStatus === 'online' && r.healthStatus !== 'suspendida')
  .sort((a, b) => b.score - a.score)
  .slice(0, 6);

export const leastActiveRadios = [...radioHealthData]
  .sort((a, b) => a.score - b.score)
  .slice(0, 6);

export const streamDownRadios = radioHealthData.filter(
  (r) => r.streamStatus !== 'online' && r.streamStatus !== 'inestable' && r.healthStatus !== 'suspendida',
);

export const pendingReviewRadios = radioHealthData.filter(
  (r) => r.warningLevel === 'advertencia' || r.warningLevel === 'oculta' || r.warningLevel === 'critica',
);

/* ─── Stream-caído specific list with progressive stages ─── */
export const streamDownDetailedRadios = radioHealthData.filter(
  (r) => r.streamStatus === 'offline' || r.streamStatus === 'error' || r.streamStatus === 'sin_respuesta',
).sort((a, b) => b.daysOffline - a.daysOffline);

/* ─── Summary KPIs ─── */
export const healthKPIs = {
  totalRadios: radioHealthData.length,
  excelentes: radioHealthData.filter((r) => r.healthStatus === 'excelente').length,
  buenas: radioHealthData.filter((r) => r.healthStatus === 'buena').length,
  regulares: radioHealthData.filter((r) => r.healthStatus === 'regular').length,
  inactivas: radioHealthData.filter((r) => r.healthStatus === 'inactiva').length,
  suspendidas: radioHealthData.filter((r) => r.healthStatus === 'suspendida').length,
  streamCaido: streamDownDetailedRadios.length,
  streamInestable: radioHealthData.filter((r) => r.streamStatus === 'inestable').length,
  pendientesRevision: pendingReviewRadios.length,
  ocultasDirectorio: radioHealthData.filter((r) => r.isHiddenFromDirectory).length,
  enRiesgo: radioHealthData.filter((r) => r.isAtRisk).length,
};

/* ─── Public directory priority sort ─── */
export function getDirectoryPriorityScore(radio: RadioHealthData): number {
  let priority = radio.score;
  if (radio.streamStatus === 'online') priority += 20;
  else if (radio.streamStatus === 'inestable') priority += 5;
  if (radio.isHiddenFromDirectory) priority = -1;
  if (radio.healthStatus === 'suspendida') priority = -2;
  if (radio.plan === 'premium') priority += 5;
  if (radio.plan === 'pro') priority += 3;
  return priority;
}